import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import Update from "./../assets/assets/update.svg";
import View from "./../assets/assets/view-icon.svg";
import DownloadIcon from "./../assets/assets/download.svg";
import File from "./../assets/assets/file.svg";
import { useSelector } from "react-redux";
import UserModal from "../components/EmailModal/UserModal";
import { uploadFile } from "./cloudinary";
import { callUrlFunction } from "./callUrlFunction";

// Helper to derive preview/embed/download from an initial url prop
function buildDerivedUrls(url) {
  const isPdf = !!url && /\.pdf($|\?)/i.test(url);

  if (!url) {
    return {
      fileResult: "",
      filePreview: "",
      fileEmbed: "",
      fileDownload: "",
      fileType: "",
    };
  }

  if (!isPdf) {
    // images: everything is the same url
    return {
      fileResult: url,
      filePreview: url,
      fileEmbed: url,
      fileDownload: url,
      fileType: "image",
    };
  }

  // PDFs: keep original as actual file, compute preview/embed/download
  const fileResult = url;
  const fileEmbed = url.replace("/raw/upload/", "/raw/upload/fl_inline/");
  const fileDownload = url.replace("/raw/upload/", "/raw/upload/fl_attachment/");
  const filePreview = url
    .replace("/raw/upload/", "/image/upload/pg_1,w_300/") // first page, width 300
    .replace(/\.pdf($|\?)/i, ".jpg$1"); // make sure it's an image extension

  return {
    fileResult,
    filePreview,
    fileEmbed,
    fileDownload,
    fileType: "pdf",
  };
}

const UpdateWidget = ({
  title,
  handleDataFetch,
  handleRemoval,
  id,
  url,
  error,
  loaded,
  name,
}) => {
  const derived = buildDerivedUrls(url);

  const [state, setState] = useState({
    loaded: loaded || !!url,
    fileResult: derived.fileResult,   // actual file (raw/pdf or image)
    filePreview: derived.filePreview, // thumbnail for pdf (jpg)
    fileEmbed: derived.fileEmbed,     // inline pdf (fl_inline)
    fileDownload: derived.fileDownload, // download link (fl_attachment)
    file_name: name || "",
    file_type: derived.fileType || (url && url.includes(".pdf") ? "pdf" : "image"),
    showModal: false,
    previewBroken: false,
  });

  // If the incoming url prop changes later, recompute derived URLs
  useEffect(() => {
    const d = buildDerivedUrls(url);
    setState((s) => ({
      ...s,
      loaded: loaded || !!url,
      fileResult: d.fileResult,
      filePreview: d.filePreview,
      fileEmbed: d.fileEmbed,
      fileDownload: d.fileDownload,
      file_type: d.fileType || (url && url.includes(".pdf") ? "pdf" : "image"),
      previewBroken: false,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const isDisabled = id !== 1;
  const success = useSelector((state) => state.singleUser.uploadSuccess);

  useEffect(() => {
    if (success) {
      setState((s) => ({
        ...s,
        fileResult: "",
        filePreview: "",
        fileEmbed: "",
        fileDownload: "",
        file_name: "",
        file_type: "",
        loaded: false,
        previewBroken: false,
      }));
    }
    // eslint-disable-next-line
  }, [success]);

  const showWidget = async () => {
    if (isDisabled) return;
    try {
      await uploadFile(callUrlFunction(handleDataFetch, id, setState, state));
    } catch (err) {
      console.error("Upload failed:", err);
    }
  };

  const removeFile = () => {
    if (isDisabled) return;
    handleRemoval({ id, url: state.fileResult });
    setState((s) => ({
      ...s,
      fileResult: "",
      filePreview: "",
      fileEmbed: "",
      fileDownload: "",
      file_name: "",
      file_type: "",
      loaded: false,
      previewBroken: false,
    }));
  };

  const viewDoc = () => {
    if (isDisabled) return;
    setState((s) => ({ ...s, showModal: true }));
  };

  const closeDoc = () => {
    setState((s) => ({ ...s, showModal: false }));
  };

  function shortenName(str) {
    return str?.length > 12 ? str.slice(0, 12) + "..." : str;
  }

  return (
    <div className={`upload-file ${isDisabled ? "disabled-widget" : ""}`}>
      {state.loaded && (
        <div className="view-doc-upload">
          <div className="view-doc-upload-image">
            <img src={View} alt="view" />
          </div>
          <span data-testid="viewDoc" onClick={viewDoc}>
            View
          </span>
          <div className="view-doc-upload-image" style={{ marginLeft: "1rem" }}>
            <a href={state.fileDownload || state.fileResult} download={state.file_name}>
              <img src={DownloadIcon} alt="download" />
            </a>
          </div>
          <span>
            <a href={state.fileDownload || state.fileResult} download={state.file_name}>
              Download
            </a>
          </span>
        </div>
      )}

      <h3>{title}</h3>

      <UserModal open={state.showModal} close={closeDoc} height={"739"} width={"1000"}>
        <div style={{ padding: "20px" }}>
          <span className="iframe-back2" data-testid="closeDoc" onClick={closeDoc}>
            X
          </span>

          {state.file_type === "pdf" ? (
            <>
              {/* Thumbnail (safe image) */}
              {!state.previewBroken && state.filePreview ? (
                <img
                  src={state.filePreview}
                  alt="PDF Preview"
                  style={{ width: "220px", marginBottom: "10px", border: "1px solid #eee" }}
                  onError={() => setState((s) => ({ ...s, previewBroken: true }))}
                />
              ) : (
                <div style={{ width: 220, height: 310, marginBottom: 10, border: "1px solid #eee", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span>Preview unavailable</span>
                </div>
              )}

              {/* Inline PDF (use fl_inline) */}
              <embed
                src={state.fileEmbed || state.fileResult}
                width="100%"
                height="1000"
                title={name}
                type="application/pdf"
              />

              {/* Fallback open-in-new-tab */}
              <div style={{ marginTop: 8 }}>
                <a href={state.fileResult} target="_blank" rel="noopener noreferrer">
                  Open in new tab
                </a>
              </div>
            </>
          ) : (
            <img
              src={state.fileResult}
              alt={name}
              style={{ width: "100%", maxHeight: "1000px", objectFit: "contain" }}
            />
          )}
        </div>
      </UserModal>

      {!state.loaded ? (
        <div>
          <div>
            <button
              className="chooseFile"
              data-testid="showWidget"
              onClick={showWidget}
              disabled={isDisabled}
            >
              + &nbsp; &nbsp; CHOOSE FILE
            </button>
          </div>
          {error !== "" && <span style={{ color: "red" }}>{error}</span>}
        </div>
      ) : (
        <div className="uploaded-file-doc">
          <span>
            <img src={File} alt="file" /> &nbsp;
            {shortenName(state.file_name)}
          </span>
          {!isDisabled && (
            <div
              className="icon-upload-flex"
              data-testid="removeFile"
              onClick={removeFile}
            >
              <div className="icon-upload">
                <img src={Update} alt="update" />
              </div>
              <h6>UPDATE</h6>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

UpdateWidget.propTypes = {
  error: PropTypes.string,
  handleDataFetch: PropTypes.func,
  handleRemoval: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loaded: PropTypes.bool,
  name: PropTypes.string,
  title: PropTypes.string,
  url: PropTypes.string,
};

export default UpdateWidget;
